import { BookingOrchestrator } from '../booking-orchestrator';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import { FunctionSwitchHelperService } from '../../../app/providers/component-helpers/function-switch.service';
import {
  CAR_MANAGEMENT,
  CM_ALLOW_CAR_RELOCATION,
  NEGATIVE_POSITIONS,
  SO_USE_BUFFERSTOCKS,
} from '../../../assets/constants/functionSwitch.constants';
import { Injectable } from '@angular/core';
import {
  BookingPosition,
  BookingResult,
  IncomingTransfer,
  BookingServices,
  BookingTransactionId,
  SystemContext,
} from '../types';
import {
  BufferstockService,
  StockMovementService,
  StockObjectService,
} from 'src/packages/warehouse';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import { UserHelper } from '../../../app/providers/helpers/user-helper.service';
import { SourceBuilder } from '../builder';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import { MachineService } from '../../../app/providers/model-services/machines/machine.service';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import { ArticleService } from '../../../app/providers/model-services/article.service';
import { BookingTransaction } from '../transaction/booking-transaction';
import { ModelAdapters } from '../interface/model-adapters';
import { TransferAcceptance } from '../acceptance/transfer-acceptance';

/**
 * Service für alle Warenverbuchungen. Lädt die Funktionsschalter in
 * einen `SystemContext`, verdrahtet die Model-Services und bietet der UI
 * Einstiegspunkte für Buchungen, Transaktionen und Empfangsbestätigungen.
 */
@Injectable({
  providedIn: 'root',
})
export class BookingOrchestratorService {
  private readonly orchestrator: BookingOrchestrator;
  private systemContext: SystemContext | null;

  constructor(
    private readonly fsHelper: FunctionSwitchHelperService,
    private readonly articleService: ArticleService,
    private readonly stockObjectService: StockObjectService,
    private readonly stockMovementService: StockMovementService,
    private readonly bufferstockService: BufferstockService,
    private readonly machineService: MachineService,
    private readonly userHelper: UserHelper,
  ) {
    this.orchestrator = new BookingOrchestrator();
    this.systemContext = null;
  }

  /**
   * Die Buchung erfolgt über drei Stages (Source → Target → Optional/Submit)
   * und wird per `submit()` ausgeführt oder per `validate()` geprüft.
   *
   * @param positions Liste der zu buchenden Positionen
   * @returns Builder, an dem Quelle, Ziel und Optionen gesetzt werden
   */
  public bookingOf(positions: BookingPosition[]): SourceBuilder {
    if (!this.loadSystemContext()) {
      throw Error('Function switches are not loaded');
    }
    return new SourceBuilder(
      this.orchestrator,
      // We do not get here if systemContext is not loaded
      // tslint:disable-next-line:no-non-null-assertion
      this.systemContext!,
      {},
      this.buildServices(),
      positions,
    );
  }

  /**
   * Liefert das `BookingTransaction`-Handle für die id, über das mehrere
   * Buchungen gemeinsam committet, zurückgerollt oder verworfen werden. Die
   * zugrunde liegende Queue entsteht lazy beim ersten Enqueue; das Handle wird
   * hier weder persistiert noch muss die Queue bereits existieren.
   *
   * @param id Kennung der Transaktion (z.B. die Auftrags-ID)
   * @returns Transaction-Handle, gebunden an den aktuellen SystemContext
   */
  public transactionFor(id: BookingTransactionId): BookingTransaction {
    if (!this.loadSystemContext()) {
      throw Error('Function switches are not loaded');
    }
    const adapters = new ModelAdapters(this.buildServices());
    return new BookingTransaction(
      id,
      adapters,
      // We do not get here if systemContext is not loaded
      // tslint:disable-next-line:no-non-null-assertion
      this.systemContext!,
      this.orchestrator,
    );
  }

  /**
   * Prüft, ob für den Auftrag noch nicht abgeschlossene Buchungen in der
   * Rollback-Queue liegen. Das signalisiert nur lokal gehaltene Fill-Arbeit:
   * Die Queue wird erst beim Auftragsabschluss committet, überdauert also den
   * Warteschlangen-Upload der Bewegungen.
   *
   * @param id Transaction-ID (= Auftrags-ID)
   * @returns `true`, wenn Buchungen anstehen.
   */
  public hasPendingTransaction(id: BookingTransactionId): boolean {
    const adapters = new ModelAdapters(this.buildServices());
    return adapters.transaction.findBookings(id).length > 0;
  }

  /**
   * Lädt alle eingehenden, noch unbestätigten Umlagerungen mit einem
   * Bufferstock als Ziel. Grundlage für die Empfangsbestätigung; online-only.
   *
   * @param bufferstockId ID des empfangenden Bufferstocks
   * @returns Liste der offenen eingehenden Umlagerungen
   */
  public async findIncomingBufferstockTransfers(
    bufferstockId: number,
  ): Promise<IncomingTransfer[]> {
    const adapters = new ModelAdapters(this.buildServices());
    return adapters.movement.findIncomingBufferstockTransfers(bufferstockId);
  }

  /**
   * Lädt alle eingehenden, noch unbestätigten Umlagerungen mit einem Fahrzeug
   * als Ziel (KFZ->KFZ-Umlagerung). Grundlage für die Empfangsbestätigung am
   * Ziel-KFZ; online-only.
   *
   * @param carId ID des empfangenden Fahrzeugs
   * @returns Liste der offenen eingehenden Umlagerungen
   */
  public async findIncomingCarTransfers(
    carId: number,
  ): Promise<IncomingTransfer[]> {
    const adapters = new ModelAdapters(this.buildServices());
    return adapters.movement.findIncomingCarTransfers(carId);
  }

  /**
   * Bestätigt eine eingehende Bufferstock-Umlagerung ganz oder teilweise
   * (Phase 2 durch den Empfänger); bei Teil-Annahme wird die Differenz zur
   * Quelle zurückgebucht. Gefahrlos wiederholbar; Details in `TransferAcceptance`.
   *
   * @param movement Eingehendes Movement aus `findIncomingBufferstockTransfers`
   * @param acceptedAmount Angenommene Menge (0 ≤ x ≤ movement.amount; 0 = Voll-Ablehnung)
   * @returns Ergebnis der Empfangsbestätigung
   */
  public async acceptIncomingBufferstockTransfer(
    movement: IncomingTransfer,
    acceptedAmount: number,
  ): Promise<BookingResult> {
    const adapters = new ModelAdapters(this.buildServices());
    return new TransferAcceptance().acceptBufferstock(
      movement,
      acceptedAmount,
      adapters,
    );
  }

  /**
   * Bestätigt eine eingehende KFZ-Umlagerung ganz oder teilweise (Phase 2 durch
   * den Ziel-KFZ-Fahrer); bei Teil-Annahme geht die Differenz zurück ans
   * Quell-KFZ. Details in `TransferAcceptance.acceptCar`.
   *
   * @param movement Eingehendes Movement aus `findIncomingCarTransfers`
   * @param acceptedAmount Angenommene Menge (0 ≤ x ≤ movement.amount)
   * @returns Ergebnis der Empfangsbestätigung
   */
  public async acceptIncomingCarTransfer(
    movement: IncomingTransfer,
    acceptedAmount: number,
  ): Promise<BookingResult> {
    const adapters = new ModelAdapters(this.buildServices());
    return new TransferAcceptance().acceptCar(movement, acceptedAmount, adapters);
  }

  /**
   * Holt liegengebliebene Phase-2-Commits zweiphasiger Buchungen nach (z.B.
   * beim App-Start nach Netzabriss zwischen den Phasen). Läuft zusätzlich
   * selbstheilend vor jeder Empfangsbestätigung.
   *
   * @returns Anzahl der weiterhin offenen Phase-2-Commits.
   */
  public async recoverPendingPhase2Commits(): Promise<number> {
    const adapters = new ModelAdapters(this.buildServices());
    return adapters.pendingCommit.retryAll();
  }

  /**
   * Aktueller SystemContext (Funktionsschalter-Sicht des Buchungsmoduls), z.B.
   * für Capability-Abfragen der UI zur Button-Sichtbarkeit.
   *
   * @returns Der SystemContext oder null, solange die Schalter nicht geladen sind.
   */
  public getSystemContext(): SystemContext | null {
    return this.loadSystemContext() ? this.systemContext : null;
  }

  private buildServices(): BookingServices {
    return {
      articleService: this.articleService,
      stockObjectService: this.stockObjectService,
      stockMovementService: this.stockMovementService,
      bufferstockService: this.bufferstockService,
      machineService: this.machineService,
      userHelper: this.userHelper,
    };
  }

  private loadSystemContext(): boolean {
    const switches = this.loadFunctionSwitches({
      carActive: CAR_MANAGEMENT,
      bufferstockActive: SO_USE_BUFFERSTOCKS,
      carRelocationActive: CM_ALLOW_CAR_RELOCATION,
      allowNegativePositionAmounts: NEGATIVE_POSITIONS,
    });
    for (const key of Object.keys(switches) as (keyof SystemContext)[]) {
      if (switches[key] === undefined) {
        return false;
      }
    }
    this.systemContext = switches;
    return true;
  }

  private loadFunctionSwitches(config: {
    [Property in keyof SystemContext]: string;
  }): Record<keyof SystemContext, boolean> {
    return this.fsHelper.load(config);
  }
}
